import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import type { Movie, NewsItem } from "@/types/content";

export const getMovieById = (id?: string): Movie | undefined =>
  movies.find((m) => m.id === id);

export const getNewsById = (id?: string): NewsItem | undefined =>
  news.find((n) => n.id === id);

export interface MovieFilter {
  type?: "movie" | "tv" | "all";
  genre?: string;
  region?: string;
  year?: string;
  sort?: "latest" | "rating" | "hot";
}

export const filterMovies = (filter: MovieFilter): Movie[] => {
  const { type = "all", genre = "全部", region = "全部", year = "全部", sort = "latest" } = filter;
  let list = [...movies];

  if (type !== "all") {
    list = list.filter((m) => m.type === type);
  }
  if (genre && genre !== "全部") {
    list = list.filter((m) => m.genres.includes(genre));
  }
  if (region && region !== "全部") {
    list = list.filter((m) => m.region === region);
  }
  if (year && year !== "全部") {
    if (year === "更早") {
      list = list.filter((m) => m.year < 2023);
    } else {
      list = list.filter((m) => m.year === Number(year));
    }
  }

  if (sort === "rating") {
    list.sort((a, b) => b.rating - a.rating);
  } else if (sort === "hot") {
    list.sort((a, b) => Number(b.hot) - Number(a.hot) || b.rating - a.rating);
  } else {
    list.sort((a, b) => b.year - a.year || b.rating - a.rating);
  }
  return list;
};

export const getRelatedMovies = (movie: Movie, count = 4): Movie[] => {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      const genreScore = m.genres.filter((g) => movie.genres.includes(g)).length;
      const castScore = m.cast.filter((c) => movie.cast.includes(c)).length;
      return { movie: m, score: genreScore * 2 + castScore * 3 };
    })
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating);
  return scored.slice(0, count).map((s) => s.movie);
};

export const getRelatedNews = (item: NewsItem, count = 3): NewsItem[] => {
  const scored = news
    .filter((n) => n.id !== item.id)
    .map((n) => {
      const shared = n.relatedMovieIds.filter((id) => item.relatedMovieIds.includes(id)).length;
      const catScore = n.category === item.category ? 1 : 0;
      return { item: n, score: shared * 2 + catScore };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.item);
};

export interface SearchResult {
  movies: Movie[];
  news: NewsItem[];
}

export const searchContent = (keyword: string): SearchResult => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return { movies: [], news: [] };

  const matchedMovies = movies.filter(
    (m) =>
      m.title.toLowerCase().includes(kw) ||
      m.genres.some((g) => g.toLowerCase().includes(kw)) ||
      m.cast.some((c) => c.toLowerCase().includes(kw)) ||
      m.director.toLowerCase().includes(kw)
  );

  const matchedNews = news.filter(
    (n) =>
      n.title.toLowerCase().includes(kw) ||
      n.summary.toLowerCase().includes(kw) ||
      n.category.toLowerCase().includes(kw)
  );

  return { movies: matchedMovies, news: matchedNews };
};

export const typeLabel = (type: "movie" | "tv" | "recommend" | "news" | "hot" | string): string => {
  const map: Record<string, string> = {
    movie: "电影",
    tv: "电视剧",
    recommend: "推荐",
    news: "资讯",
    hot: "热播",
    all: "全部",
  };
  return map[type] || type;
};